import { gql, useQuery } from "@apollo/client";
import { useMemo, useState, useEffect } from "react";

class PresignedUrlCache {
  private cache = new Map<string, { url: string; timestamp: number }>();
  private pendingRequests = new Map<string, Promise<string>>();
  private readonly CACHE_DURATION = 50 * 60 * 1000;

  get(key: string): string | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.url;
    }
    return null;
  }

  set(key: string, url: string): void {
    this.cache.set(key, {
      url,
      timestamp: Date.now(),
    });
  }

  clearCache(key: string): void {
    this.cache.delete(key);
  }

  hasPendingRequest(key: string): boolean {
    return this.pendingRequests.has(key);
  }

  setPendingRequest(key: string, promise: Promise<string>): void {
    this.pendingRequests.set(key, promise);
  }

  getPendingRequest(key: string): Promise<string> | undefined {
    return this.pendingRequests.get(key);
  }

  clearPendingRequest(key: string): void {
    this.pendingRequests.delete(key);
  }

  cleanup(): void {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp > this.CACHE_DURATION) {
        this.cache.delete(key);
      }
    }
  }
}

const presignedUrlCache = new PresignedUrlCache();

setInterval(() => {
  presignedUrlCache.cleanup();
}, 10 * 60 * 1000);

export const clearPresignedUrlCache = (url: string) => {
  presignedUrlCache.clearCache(url);
};

const imageRefreshCounts = new Map<string, number>();

export const refreshImage = (imageUrl: string) => {
  const currentCount = imageRefreshCounts.get(imageUrl) || 0;
  imageRefreshCounts.set(imageUrl, currentCount + 1);

  clearPresignedUrlCache(imageUrl);

  const images = document.querySelectorAll(`img[src*="${imageUrl}"]`);
  images.forEach((img) => {
    const currentSrc = img.getAttribute("src");
    if (currentSrc) {
      const newImg = new Image();
      newImg.onload = () => {
        img.setAttribute("src", currentSrc);
      };
      newImg.src = currentSrc;
    }
  });

  setTimeout(() => {
    images.forEach((img) => {
      const currentSrc = img.getAttribute("src");
      if (currentSrc) {
        img.setAttribute("src", "");
        setTimeout(() => {
          img.setAttribute("src", currentSrc);
        }, 50);
      }
    });
  }, 100);

  window.dispatchEvent(
    new CustomEvent("imageRefresh", { detail: { imageUrl } })
  );
};

export const getImageRefreshCount = (imageUrl: string): number => {
  return imageRefreshCounts.get(imageUrl) || 0;
};

export const useImageKey = (imageUrl: string | null | undefined) => {
  const [refreshCount, setRefreshCount] = useState(() =>
    getImageRefreshCount(imageUrl || "")
  );

  const imageKey = useMemo(() => {
    if (!imageUrl) return `empty-${refreshCount}`;
    return `${imageUrl}-${refreshCount}`;
  }, [imageUrl, refreshCount]);

  useEffect(() => {
    const handleImageRefresh = (event: CustomEvent) => {
      if (event.detail.imageUrl === imageUrl) {
        setRefreshCount(getImageRefreshCount(imageUrl || ""));
      }
    };

    window.addEventListener(
      "imageRefresh",
      handleImageRefresh as EventListener
    );
    return () => {
      window.removeEventListener(
        "imageRefresh",
        handleImageRefresh as EventListener
      );
    };
  }, [imageUrl]);

  return { imageKey };
};

export const usePresignedUrl = (url: string | null | undefined) => {
  const cachedUrl = useMemo(() => {
    if (!url) return null;
    return presignedUrlCache.get(url);
  }, [url]);

  const { data, loading, error } = useQuery(
    gql`
      query preSignedUrl($objectKey: String!) {
        preSignedUrl(objectKey: $objectKey)
      }
    `,
    {
      variables: {
        objectKey: url,
      },
      skip: !url || !!cachedUrl,
      fetchPolicy: "no-cache",
      errorPolicy: "ignore",
    }
  );

  const imageUrl = useMemo(() => {
    if (cachedUrl) return cachedUrl;

    if (data?.preSignedUrl) {
      presignedUrlCache.set(url!, data.preSignedUrl);
      return data.preSignedUrl;
    }

    return null;
  }, [data?.preSignedUrl, cachedUrl, url]);

  return {
    imageUrl,
    loading: loading && !cachedUrl,
    error,
  };
};

export default usePresignedUrl;
